import { useEffect, useState } from 'react';
import { Amplitude, Events } from '@iwoca/frontend-tracking-library';
import { Accordion, Button } from '@iwoca/orion';
import { t } from '@lingui/macro';
import clsx from 'clsx';
import { Card } from 'components/Card/Card';
import { LayoutHeadings } from 'components/Layout/InnerLayout/LayoutHeadings/LayoutHeadings';
import { useLocale } from 'hooks/useLocale';
import { TFAQsData } from 'types';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import styles from './FAQs.module.css';
import { FAQsData } from './FAQsData';

export const FAQs = () => {
  const { locale, isDE } = useLocale();
  const fragment = window.location.hash.substring(1);
  const [selectedGroup, setSelectedGroup] = useState<TFAQsData>(FAQsData()[0]);
  const FAQs = FAQsData();

  const questionsToDisplay = isDE
    ? selectedGroup.questions?.filter((question) => question.showInDE)
    : selectedGroup.questions;

  const selectGroup = (group: TFAQsData) => {
    setSelectedGroup(group);
    Amplitude.track(
      Events.introducerPortal.introducerPortalEventCreator({
        event: AMPLITUDE_EVENTS.faqCategorySelected,
        locale,
        payload: { Group: group.topic },
      }),
    );
  };

  const selectQuestion = (question: string | JSX.Element) => {
    Amplitude.track(
      Events.introducerPortal.introducerPortalEventCreator({
        event: AMPLITUDE_EVENTS.faqQuestionSelected,
        locale,
        payload: { Question: question },
      }),
    );
  };

  const renderHeading = () => (
    <LayoutHeadings
      heading={t`Frequently asked questions`}
      subHeading={t`Need help? Check out our most frequently asked questions`}
      headingAlignment="center"
    />
  );

  useEffect(() => {
    // Find if the url fragment matches any of the groups from the FAQs data
    const groupToSelectFromURL = FAQs.find((group) => {
      return group.id === fragment;
    });

    // If there is a match, automatically show the group
    if (groupToSelectFromURL) {
      setSelectedGroup(groupToSelectFromURL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fragment]);

  return (
    <div className={styles.FAQsContainer}>
      {renderHeading()}
      <div className={styles.FAQs}>
        <div className={styles.category}>
          <Card className={styles.card}>
            {FAQs.map((group) => (
              <Button
                key={group.topic}
                size="large"
                className={clsx(
                  styles.topicButton,
                  group.topic === selectedGroup.topic && styles.selectedButton,
                )}
                variant="tertiary"
                onClick={() => selectGroup(group)}
              >
                {group.icon}
                {group.topic}
              </Button>
            ))}
          </Card>
          <div className={styles.questionsContainer}>
            {questionsToDisplay?.map((item) => (
              <Accordion
                onClick={() => selectQuestion(item.id)}
                className={styles.question}
                title={item.question}
                key={item.id}
              >
                {item.answer}
              </Accordion>
            ))}
          </div>
        </div>
      </div>
      {isDE && (
        <div className={styles.tutorialVideo}>
          <h2 className={styles.heading}>
            So funktioniert unser Partnerportal
          </h2>
          <iframe
            className={styles.video}
            src="https://www.youtube.com/embed/s5puoaGKo1Q"
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            data-testid="youtube-video"
          ></iframe>
        </div>
      )}
    </div>
  );
};
